import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getUsageSummary } from "../lib/api/usage";
import type { UsageSummary } from "../types/usage";

interface UsageContextType {
  usage: UsageSummary | null;
  isLoading: boolean;
  error: string | null;
  hasSubscription: boolean;
  isAdminManaged: boolean;
  refreshUsage: () => Promise<void>;
  updateRemainingFromResponse: (remaining: number) => void;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export function UsageProvider({ children }: { children: ReactNode }) {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(true);
  const [isAdminManaged, setIsAdminManaged] = useState(false);
  const { clerkUser } = useAuth();

  const refreshUsage = useCallback(async () => {
    if (!clerkUser) return;
    try {
      setIsLoading(true);
      setError(null);
      const summary = await getUsageSummary();
      setUsage(summary);
      setHasSubscription(true);
      setIsAdminManaged(!summary.is_stripe_managed);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setHasSubscription(false);
        setUsage(null);
      } else {
        setError(err?.message || "Failed to load usage");
      }
    } finally {
      setIsLoading(false);
    }
  }, [clerkUser]);

  const updateRemainingFromResponse = useCallback((remaining: number) => {
    setUsage((prev) => (prev ? { ...prev, remaining, used: prev.limit - remaining } : prev));
  }, []);

  // Initial fetch
  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  // Periodic refresh every 60 seconds
  useEffect(() => {
    if (!clerkUser) return;
    const interval = setInterval(refreshUsage, 60_000);
    return () => clearInterval(interval);
  }, [clerkUser, refreshUsage]);

  // Refresh on window focus (e.g. returning from Stripe checkout tab)
  useEffect(() => {
    const onFocus = () => { refreshUsage(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUsage]);

  return (
    <UsageContext.Provider value={{ usage, isLoading, error, hasSubscription, isAdminManaged, refreshUsage, updateRemainingFromResponse }}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage(): UsageContextType {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error("useUsage must be used within a UsageProvider");
  }
  return context;
}
